import { useState, useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Collapse } from '@mui/material'

import { ArrowCircleLeftIcon, ArrowCircleRightIcon, CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'
import { Autocomplete } from '@components/shared/Autocomplete'
import { Button } from '@components/shared/Button'
import { getPagePermissions } from '@hooks/useUser/provider'
import { getPermissionValue } from './helpers/getPermissionValue'
import { PageIds } from '@hooks/useLayout/types'
import { permissionActionsLIst } from '@pages/AccessProfile/helpers/permissionActionsLIst'
import { removeAccents } from '@utils/functions/removeAccents'
import { setInitialValues } from '../AccessProfileModal/form/setInitialValues'
import { Switch } from '@components/shared/Switch'
import { Title } from '@components/shared/Title'
import { useAccessProfile } from '@pages/AccessProfile/context'

import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'
import type { PermissionDataProps, PermissionSettingsProps } from './types'

import * as S from './styles'
import { useUser } from '@hooks/useUser'

export const PermissionSettings = ({ data }: PermissionSettingsProps) => {
  const { setValue, watch, reset } = useFormContext()

  const { userPermissionsData } = useUser()
  const { accessProfileByIdData } = useAccessProfile()

  const [menuData, setMenuData] = useState<PermissionsItemProps | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [openMenus, setOpenMenus] = useState<Record<number, boolean>>({})
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)

  const [permissionsData, setPermissionsData] = useState<PermissionDataProps | null>(null)

  const filteredMenus = useMemo(() => {
    if (!searchValue.trim()) return accessProfileByIdData?.permissions

    const searchNormalized = removeAccents(searchValue)

    return accessProfileByIdData
      ?.permissions!.map((item) => {
        const parentMatches = removeAccents(item.name).includes(searchNormalized)

        const filteredChildren = item.children?.filter((child) => removeAccents(child.name).includes(searchNormalized))

        if (parentMatches || (filteredChildren && !!filteredChildren.length)) {
          return { ...item, children: parentMatches ? item.children : filteredChildren }
        }

        return null
      })
      .filter((item) => item !== null) as PermissionsItemProps[]
  }, [accessProfileByIdData, searchValue])

  const flatMenuList = useMemo(() => {
    const list: string[] = []

    accessProfileByIdData?.permissions.forEach((item) => {
      if (!item.children?.length) {
        list.push(item.name)
      } else {
        item.children.forEach((child) => {
          list.push(child.name)
        })
      }
    })
    return list
  }, [accessProfileByIdData])

  const findMenuByName = (name: string): PermissionsItemProps | null => {
    for (const item of accessProfileByIdData?.permissions || []) {
      if (item.name === name) {
        return item
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.name === name) {
            return child
          }
        }
      }
    }
    return null
  }

  const currentIndex = useMemo(() => {
    const index = flatMenuList.findIndex((menu) => menu === menuData?.name)
    return index === -1 ? 0 : index
  }, [flatMenuList, menuData])

  const handlePrevious = () => {
    if (currentIndex > 0 && flatMenuList.length > 0) {
      const menuName = flatMenuList[currentIndex - 1]
      const menu = findMenuByName(menuName)
      if (menu) {
        setMenuData(menu)
      }
    }
  }

  const handleNext = () => {
    if (currentIndex < flatMenuList.length - 1 && flatMenuList.length > 0) {
      const menuName = flatMenuList[currentIndex + 1]
      const menu = findMenuByName(menuName)
      if (menu) {
        setMenuData(menu)
      }
    }
  }

  const isActionDisabled = (menuId: number, index: number) => {
    if ([15].includes(menuId) && [0, 1].includes(index)) return true
    if ([9].includes(menuId) && [0, 1, 3, 5].includes(index)) return true

    return false
  }

  const handleEditPermission = (menuId: number, index: number, checked: boolean) => {
    const newPermissionsData = permissionsData?.[menuId]

    if (!newPermissionsData) return

    if ([9].includes(menuId) && [2].includes(index)) {
      newPermissionsData[2].value = checked
      newPermissionsData[4].value = true
    } else if ([4].includes(index) && !checked && (!!newPermissionsData[0].value || !!newPermissionsData[2].value)) {
      newPermissionsData[0].value = checked
      newPermissionsData[2].value = checked
      newPermissionsData[3].value = checked
      newPermissionsData[4].value = checked
      newPermissionsData[5].value = checked
    } else if (![9, 15].includes(menuId) && [2, 3, 5].includes(index)) {
      newPermissionsData[2].value = checked
      newPermissionsData[3].value = checked
      newPermissionsData[4].value = checked
      newPermissionsData[5].value = checked
      newPermissionsData[0].value = false
    } else if ([0].includes(index)) {
      newPermissionsData[0].value = checked
      newPermissionsData[1].value = checked
      newPermissionsData[2].value = checked
      newPermissionsData[3].value = checked
      newPermissionsData[4].value = checked
      newPermissionsData[5].value = checked
    } else {
      newPermissionsData[index].value = checked
      newPermissionsData[0].value = false
    }

    const permissions = {
      ...permissionsData,
      [menuId]: newPermissionsData
    }

    setPermissionsData(permissions)
    setValue('permissions', permissions)
  }

  const handleSetInitialValues = () => {
    reset(setInitialValues(data, accessProfileByIdData))

    let menus: PermissionDataProps = {}

    accessProfileByIdData?.permissions.forEach((permissionItem) => {
      if (!permissionItem.children?.length) {
        menus = {
          ...menus,
          [permissionItem.id]: permissionActionsLIst.map((item, index) => ({
            icon: item.icon,
            label: item.label,
            value: getPermissionValue(index, permissionItem)
          }))
        }
      } else {
        permissionItem.children.forEach((childPermissionItem) => {
          menus = {
            ...menus,
            [childPermissionItem.id]: permissionActionsLIst.map((item, index) => ({
              icon: item.icon,
              label: item.label,
              value: getPermissionValue(index, childPermissionItem)
            }))
          }
        })
      }
    })

    setPermissionsData(menus)
    setValue('permissions', menus)
  }

  useEffect(() => {
    !!data && handleSetInitialValues()
  }, [data])

  useEffect(() => {
    if (!!accessProfileByIdData?.permissions?.length) {
      setMenuData(accessProfileByIdData.permissions[0]?.children?.[0] || null)
    }
  }, [accessProfileByIdData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.ACCESS_PROFILE)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return (
    <S.Container>
      <S.MenuContent>
        <S.Search>
          <Autocomplete placeholder="Buscar Módulos" options={[]} onChange={(value) => setSearchValue(value)} />
        </S.Search>

        <S.Menus style={{ paddingBottom: 32 }}>
          {filteredMenus?.map((item) => {
            const isOpen = openMenus[item.id] !== false

            return (
              <>
                <S.Menu
                  key={item.id}
                  onClick={() => {
                    setOpenMenus((prev) => ({
                      ...prev,
                      [item.id]: !isOpen
                    }))
                    !item.children?.length && setMenuData(item)
                  }}
                  isSelected={menuData?.name === item.name}
                >
                  {item.name}
                  {item.children?.length ? (
                    <S.ArrowIcon>{isOpen ? <CaretUpIcon /> : <CaretDownIcon />}</S.ArrowIcon>
                  ) : null}
                </S.Menu>

                {item.children?.length && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <S.Menus>
                      {item.children?.map((child) => (
                        <S.SubMenu
                          key={child.id}
                          onClick={() => {
                            setMenuData(child)
                          }}
                          isSelected={menuData?.name === child.name}
                        >
                          {child.name}
                        </S.SubMenu>
                      ))}
                    </S.Menus>
                  </Collapse>
                )}
              </>
            )
          })}
        </S.Menus>
      </S.MenuContent>

      <S.Content>
        <S.TopContent>
          <Title label={menuData?.name || ''} marginTop={0} marginBottom={8} />

          {permissionsData?.[menuData?.id!]?.map((item, index) => (
            <S.Options key={item?.label}>
              <S.Label>
                {item.icon} {item.label}
              </S.Label>

              <Switch
                name={`permissions.${menuData?.id}.${item.label}`}
                checked={item.value}
                onChange={(_, checked) => handleEditPermission(menuData?.id!, index, checked)}
                disabled={watch('fullAccess') || isActionDisabled(menuData?.id!, index) || !permissions?.canEdit}
              />
            </S.Options>
          ))}
        </S.TopContent>

        <S.BottomContent>
          <Button
            width={165}
            colorType="outlineBlue"
            rightIcon={<ArrowCircleLeftIcon size={24} />}
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            Voltar
          </Button>
          <Button
            width={165}
            colorType="outlineBlue"
            rightIcon={<ArrowCircleRightIcon size={24} />}
            onClick={handleNext}
            disabled={currentIndex === flatMenuList.length - 1}
          >
            Próximo
          </Button>
        </S.BottomContent>
      </S.Content>
    </S.Container>
  )
}
